import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Home.css';

function Home() {
  const navigate = useNavigate();
  const user = localStorage.getItem('user');

  useEffect(() => {
    if (user) navigate('/home');
  }, [user, navigate]);

  return (
    <div className="home-page">

      {/* NAV */}
      <nav className="home-nav">
        <div className="container d-flex justify-content-between align-items-center">
          <span className="nav-logo">Zip<span>QA</span></span>
          <div className="d-flex gap-2 align-items-center">
            <Link to="/support" className="btn-nav-ghost">Support</Link>
            <Link to="/login" className="btn-nav-primary">Sign In</Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="hero-section">
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <div className="hero-badge">✦ QA Management Reimagined</div>
              <h1 className="hero-title">Ship Quality.<br />Ship Fast.</h1>
              <p className="hero-subtitle">
                ZipQA gives your team one place to write, run, and track every test —
                from manual checks to full CI/CD pipelines.
              </p>
              <div className="hero-buttons">
                <Link to="/login" className="btn-hero-primary">Sign In</Link>
                <a href="#features" className="btn-hero-ghost">See Features ↓</a>
              </div>
              <div className="hero-stats">
                <div className="stat">
                  <span className="stat-number">CI/CD</span>
                  <span className="stat-label">Ready</span>
                </div>
                <div className="stat-divider" />
                <div className="stat">
                  <span className="stat-number">REST</span>
                  <span className="stat-label">API First</span>
                </div>
                <div className="stat-divider" />
                <div className="stat">
                  <span className="stat-number">JIRA</span>
                  <span className="stat-label">Integrated</span>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="hero-visual">
                <div className="mock-window">
                  <div className="mock-titlebar">
                    <span className="dot red" /><span className="dot yellow" /><span className="dot green" />
                    <span className="mock-url">zipqa.app / runs</span>
                  </div>
                  <div className="mock-body">
                    <div className="mock-sidebar">
                      <div className="mock-nav-item active">🚀 Test Runs</div>
                      <div className="mock-nav-item">📋 Test Cases</div>
                      <div className="mock-nav-item">📁 Suites</div>
                      <div className="mock-nav-item">�� Reports</div>
                    </div>
                    <div className="mock-content">
                      <div className="mock-run-header">
                        <span className="mock-run-title">Sprint 42 — iOS Regression</span>
                        <span className="mock-badge running">● In Progress</span>
                      </div>
                      <div className="mock-progress-row">
                        <div className="mock-progress-bar">
                          <div className="mock-progress-fill" style={{ width: '68%' }} />
                        </div>
                        <span className="mock-progress-label">68% complete</span>
                      </div>
                      <div className="mock-results">
                        <div className="mock-result-row"><span className="mock-status pass">✓</span><span>Login flow – iOS 17</span><span className="mock-time">2m 14s</span></div>
                        <div className="mock-result-row"><span className="mock-status pass">✓</span><span>Checkout – Happy path</span><span className="mock-time">1m 47s</span></div>
                        <div className="mock-result-row"><span className="mock-status run">●</span><span>Push notifications</span><span className="mock-time">running</span></div>
                        <div className="mock-result-row"><span className="mock-status queued">○</span><span>Offline mode sync</span><span className="mock-time">queued</span></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="features-section">
        <div className="container">
          <div className="section-header">
            <p className="section-eyebrow">Features</p>
            <h2 className="section-title">Everything your QA team needs</h2>
            <p className="section-subtitle">Powerful tools built for modern engineering teams — no bloat, no lock-in.</p>
          </div>

          {/* Feature 1 — Test Runs */}
          <div className="feature-row">
            <div className="feature-row-visual">
              <div className="fmock-window">
                <div className="fmock-bar">
                  <span className="dot red" /><span className="dot yellow" /><span className="dot green" />
                  <span className="fmock-url">Test Runs</span>
                </div>
                <div className="fmock-body">
                  <div className="fmock-run-item fmock-active">
                    <span className="fmock-dot fmock-run">●</span>
                    <div className="fmock-run-info">
                      <div className="fmock-run-name">Sprint 42 — iOS Regression</div>
                      <div className="fmock-run-meta">12 / 18 passed · Running</div>
                    </div>
                    <span className="fmock-tag in-progress">In Progress</span>
                  </div>
                  <div className="fmock-run-item">
                    <span className="fmock-dot fmock-pass">●</span>
                    <div className="fmock-run-info">
                      <div className="fmock-run-name">Smoke — Android v14</div>
                      <div className="fmock-run-meta">32 / 32 passed · 4m 12s</div>
                    </div>
                    <span className="fmock-tag passed">Passed</span>
                  </div>
                  <div className="fmock-run-item">
                    <span className="fmock-dot fmock-fail">●</span>
                    <div className="fmock-run-info">
                      <div className="fmock-run-name">Regression — Web Chrome</div>
                      <div className="fmock-run-meta">41 / 50 passed · 3 failures</div>
                    </div>
                    <span className="fmock-tag failed">Failed</span>
                  </div>
                  <div className="fmock-progress-section">
                    <div className="fmock-progress-label-row">
                      <span>Sprint 42 Progress</span><span>68%</span>
                    </div>
                    <div className="fmock-progress-bar">
                      <div className="fmock-progress-fill" style={{ width: '68%' }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="feature-row-text">
              <span className="feature-row-badge" style={{ background: '#ede9fe', color: '#7c3aed' }}>Core Feature</span>
              <h3>Test Runs with Real-Time Tracking</h3>
              <p>Create manual or automated test runs with full device configuration — platform, OS version, build number, environment. Watch every test update live.</p>
              <ul className="feature-bullets">
                <li>Device &amp; environment config per run</li>
                <li>Per-test status updates in real time</li>
                <li>Assign testers, set due dates</li>
                <li>CI/CD triggered runs via REST API</li>
              </ul>
            </div>
          </div>

          {/* Feature 2 — Test Suites */}
          <div className="feature-row feature-row-reverse">
            <div className="feature-row-visual">
              <div className="fmock-window">
                <div className="fmock-bar">
                  <span className="dot red" /><span className="dot yellow" /><span className="dot green" />
                  <span className="fmock-url">Test Suites</span>
                </div>
                <div className="fmock-body">
                  <div className="fmock-suite-grid">
                    <div className="fmock-suite-card">
                      <div className="fmock-suite-icon" style={{ background: '#dcfce7' }}>📁</div>
                      <div className="fmock-suite-name">Smoke Tests</div>
                      <div className="fmock-suite-count">18 cases</div>
                      <div className="fmock-suite-bar"><div style={{ width: '100%', background: '#16a34a' }} /></div>
                    </div>
                    <div className="fmock-suite-card">
                      <div className="fmock-suite-icon" style={{ background: '#fef3c7' }}>📁</div>
                      <div className="fmock-suite-name">Regression</div>
                      <div className="fmock-suite-count">64 cases</div>
                      <div className="fmock-suite-bar"><div style={{ width: '82%', background: '#d97706' }} /></div>
                    </div>
                    <div className="fmock-suite-card">
                      <div className="fmock-suite-icon" style={{ background: '#ede9fe' }}>📁</div>
                      <div className="fmock-suite-name">Login &amp; Auth</div>
                      <div className="fmock-suite-count">12 cases</div>
                      <div className="fmock-suite-bar"><div style={{ width: '100%', background: '#7c3aed' }} /></div>
                    </div>
                    <div className="fmock-suite-card">
                      <div className="fmock-suite-icon" style={{ background: '#fee2e2' }}>📁</div>
                      <div className="fmock-suite-name">Checkout Flow</div>
                      <div className="fmock-suite-count">9 cases</div>
                      <div className="fmock-suite-bar"><div style={{ width: '67%', background: '#dc2626' }} /></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="feature-row-text">
              <span className="feature-row-badge" style={{ background: '#dcfce7', color: '#16a34a' }}>Organization</span>
              <h3>Organize with Test Suites</h3>
              <p>Group related test cases into suites for logical organization. Run an entire suite with one click and get aggregated pass rates across all included cases.</p>
              <ul className="feature-bullets">
                <li>Nest suites inside projects</li>
                <li>One-click suite execution</li>
                <li>Per-suite pass rate reporting</li>
                <li>Bulk import cases via CSV</li>
              </ul>
            </div>
          </div>

          {/* Feature 3 — CI/CD */}
          <div className="feature-row">
            <div className="feature-row-visual">
              <div className="fmock-window">
                <div className="fmock-bar">
                  <span className="dot red" /><span className="dot yellow" /><span className="dot green" />
                  <span className="fmock-url">CI/CD Integration</span>
                </div>
                <div className="fmock-body fmock-code-body">
                  <div className="fmock-code-section">
                    <div className="fmock-code-label">REST API — Push Results</div>
                    <div className="fmock-code-block">
                      <div className="fmock-code-line"><span className="fmock-kw">POST</span> /api/runs/<span className="fmock-val">{'{runId}'}</span>/results</div>
                      <div className="fmock-code-line"><span className="fmock-comment">Authorization: ApiKey </span><span className="fmock-str">zqa_••••••••</span></div>
                      <div className="fmock-code-line fmock-comment">Content-Type: application/json</div>
                    </div>
                  </div>
                  <div className="fmock-pipeline-steps">
                    <div className="fmock-step done">✓ Build</div>
                    <div className="fmock-step-arrow">→</div>
                    <div className="fmock-step done">✓ Unit Tests</div>
                    <div className="fmock-step-arrow">→</div>
                    <div className="fmock-step active">⟳ ZipQA</div>
                    <div className="fmock-step-arrow">→</div>
                    <div className="fmock-step pending">Deploy</div>
                  </div>
                  <div className="fmock-result-summary">
                    <span className="fmock-sum fmock-pass-sum">✓ 41 passed</span>
                    <span className="fmock-sum fmock-fail-sum">✗ 3 failed</span>
                    <span className="fmock-sum fmock-skip-sum">– 6 skipped</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="feature-row-text">
              <span className="feature-row-badge" style={{ background: '#fef3c7', color: '#d97706' }}>Automation</span>
              <h3>CI/CD Pipeline Integration</h3>
              <p>Push test results directly from Jenkins, GitHub Actions, GitLab CI, or any tool via our REST API. API key auth is built-in and takes minutes to configure.</p>
              <ul className="feature-bullets">
                <li>REST API with API key authentication</li>
                <li>Works with any CI/CD tool</li>
                <li>Block deployments on test failures</li>
                <li>Full OpenAPI / Swagger documentation</li>
              </ul>
            </div>
          </div>

          {/* Feature 4 — Reports */}
          <div className="feature-row feature-row-reverse">
            <div className="feature-row-visual">
              <div className="fmock-window">
                <div className="fmock-bar">
                  <span className="dot red" /><span className="dot yellow" /><span className="dot green" />
                  <span className="fmock-url">Reports &amp; Analytics</span>
                </div>
                <div className="fmock-body">
                  <div className="fmock-stats-row">
                    <div className="fmock-stat-box" style={{ borderColor: '#16a34a' }}>
                      <div className="fmock-stat-num" style={{ color: '#16a34a' }}>94%</div>
                      <div className="fmock-stat-lbl">Pass Rate</div>
                    </div>
                    <div className="fmock-stat-box" style={{ borderColor: '#667eea' }}>
                      <div className="fmock-stat-num" style={{ color: '#667eea' }}>1,248</div>
                      <div className="fmock-stat-lbl">Tests Run</div>
                    </div>
                    <div className="fmock-stat-box" style={{ borderColor: '#d97706' }}>
                      <div className="fmock-stat-num" style={{ color: '#d97706' }}>12</div>
                      <div className="fmock-stat-lbl">Open Bugs</div>
                    </div>
                  </div>
                  <div className="fmock-chart">
                    <div className="fmock-chart-label">Weekly Pass Rate</div>
                    <div className="fmock-bars">
                      {[72, 85, 78, 91, 88, 94, 94].map((h, i) => (
                        <div key={i} className="fmock-bar-col">
                          <div className="fmock-bar-fill" style={{ height: `${h}%`, background: i === 6 ? '#667eea' : '#c7d2fe' }} />
                        </div>
                      ))}
                    </div>
                    <div className="fmock-chart-days">
                      {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => <span key={i}>{d}</span>)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="feature-row-text">
              <span className="feature-row-badge" style={{ background: '#e0f2fe', color: '#0284c7' }}>Analytics</span>
              <h3>Reports &amp; Visual Dashboards</h3>
              <p>Track quality trends over time. See pass rates, coverage gaps, and sprint-over-sprint improvements at a glance with beautiful visual dashboards.</p>
              <ul className="feature-bullets">
                <li>Pass/fail trend charts by sprint</li>
                <li>Per-suite and per-tester breakdown</li>
                <li>Export reports as CSV</li>
                <li>Filter by date, project, environment</li>
              </ul>
            </div>
          </div>

        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="how-section">
        <div className="container">
          <div className="section-header">
            <p className="section-eyebrow">Workflow</p>
            <h2 className="section-title">From zero to tested in minutes</h2>
            <p className="section-subtitle">Get your entire QA process running in an afternoon.</p>
          </div>
          <div className="steps-row">
            <div className="step">
              <div className="step-number">01</div>
              <h4>Create a Project</h4>
              <p>Set up your project and invite your team in seconds.</p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step">
              <div className="step-number">02</div>
              <h4>Write Test Cases</h4>
              <p>Add cases manually or bulk-import via CSV.</p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step">
              <div className="step-number">03</div>
              <h4>Launch a Run</h4>
              <p>Pick suites, configure the device, assign a tester.</p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step">
              <div className="step-number">04</div>
              <h4>Track Results</h4>
              <p>Real-time pass/fail with JIRA bug creation on the fly.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-box">
            <h2 className="cta-title">Ready to level up your QA?</h2>
            <p className="cta-subtitle">Built for modern engineering teams that care about quality.</p>
            <div className="d-flex gap-3 justify-content-center flex-wrap">
              <Link to="/login" className="btn-cta-primary">Sign In</Link>
              <Link to="/support" className="btn-cta-ghost">Contact Support</Link>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="footer">
        <div className="container">
          <div className="row g-4">
            <div className="col-md-4">
              <span className="footer-logo">Zip<span>QA</span></span>
              <p className="footer-text mt-3">Modern test management platform for teams that care about quality.</p>
            </div>
            <div className="col-md-4">
              <h6 className="footer-heading">Product</h6>
              <ul className="footer-links">
                <li><a href="#features">Features</a></li>
              </ul>
            </div>
            <div className="col-md-4">
              <h6 className="footer-heading">Help</h6>
              <ul className="footer-links">
                <li><Link to="/login">Sign In</Link></li>
                <li><Link to="/support">Support</Link></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© 2026 ZipQA by AlphaworksAI. All rights reserved.</p>
          </div>
        </div>
      </footer>

    </div>
  );
}

export default Home;
